import React, { useRef, useState, useEffect } from "react";
import {
  Button,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import volunteerTabsStyles from "modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import { queryClient } from "api/queryClient";
import { useVolunteerService } from "../api/useVolunteerQuery";
import VolunteerICF from "modules/Volunteer/ICF/pages/VolunteerICF";
import VolunteerTests from "modules/Volunteer/VolunteerTest/pages/VolunteerTestsList";
import ViewVolunteerDetails from "../pages/ViewVolunteerDetails";
import VolunteerDashboard from "../pages/VolunteerDashboard";
import VolunteerStudyHistoryList from "modules/Volunteer/AssignedStudy/pages/VolunteerStudyHistoryList";
import VolunteerStudyDashboard from "modules/Volunteer/AssignedStudy/pages/VolunteerStudyDashboard";
import { VOLUNTEER_PATHS } from "Routes/paths";
import RouteGuard from "Routes/RouteGuard";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import VolunteerBlockTab from "modules/Volunteer/BlockVolunteer/components/VolunteerBlockTab";
import { VolunteerTab, VolunteerTabKey } from "../helper/volunteer.types";
/* ---------------- Types ---------------- */


/* ---------------- Component ---------------- */

const VolunteerInfoTab: React.FC = () => {
  document.title = "Volunteer Details | ZENTIXS VISTA";

  const [activeTab, setActiveTab] = useState<VolunteerTabKey>("dashboard");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const segmentTabsRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const VolunteerName = sessionStorage.getItem("volunteerName") || "";
  const allTabs: VolunteerTab[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: "ri-dashboard-line",
      path: "",
    },
    {
      key: "detail",
      label: "Details",
      icon: "ri-file-list-3-line",
      path: "detail",
    },
    {
      key: "rcf",
      label: "Consent (RCF)",
      icon: "ri-shield-check-line",
      path: "rcf",
    },
    {
      key: "testresults",
      label: "Test Results",
      icon: "ri-file-chart-line",
      path: "testresults",
    },
    {
      key: "study",
      label: "Study",
      icon: "ri-book-open-line",
      path: "study",
    },
    {
      key: "block",
      label: "Block",
      icon: "ri-user-forbid-line",
      path: "block",
    },
  ];

  const tabs = allTabs.filter(
    (tab) => tab.key !== "block" || sessionStorage.getItem("volunteerType") === "available"
  );

  const [slider, setSlider] = useState({
    left: "2px",
    width: "80px",
  });

  /* ---------- Volunteer View API call  ---------- */

  const { id } = useParams();

  const { SelectQuery } = useVolunteerService({
    id: Number(id),
  });

  const volunteerData = SelectQuery?.data?.data?.[0];
  const isLoading = SelectQuery?.isLoading || SelectQuery?.isFetching;
  const alcoholTest = volunteerData?.alcoholtest?.[0];
  const volunteerId = volunteerData?.volunteerid;
  const alcoholStatus = alcoholTest?.alcoholresulttext;

  /* ---------- Sync tab with URL ---------- */

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (parts.includes("study")) {
      setActiveTab("study");
    } else if (last === id) {
      setActiveTab("dashboard");
    } else {
      const match = tabs.find((t) => t.key === last);
      setActiveTab(match ? match.key : "dashboard");
    }
  }, [location.pathname, id]);

  /* ---------- Tab click ---------- */

  const toggleTab = (tabKey: VolunteerTabKey, index: number) => {
    const targetPath = tabs[index].path;
    navigate(`/volunteer/details/${id}/${targetPath}`);
    setActiveTab(tabKey);

    const el = tabRefs.current[index];
    const padding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + padding + "px",
        width: el.offsetWidth + "px",
      });
    }
  };

  /* ---------- Slider reposition ---------- */

  useEffect(() => {
    const index = tabs.findIndex((t) => t.key === activeTab);
    const el = tabRefs.current[index];

    const padding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + padding + "px",
        width: el.offsetWidth + "px",
      });
    }
  }, [activeTab]);

  // FIX 1: correct spelling
  const isAlcoholFailed = alcoholStatus === "positive";

  // FIX 2: safe chaining

  const isRestricted = isAlcoholFailed;

  const isTabLocked = (tabKey: VolunteerTabKey) => {
    if (isRestricted) {
      const restrictedTabs: VolunteerTabKey[] = [
        "study",
        "screening",
        "testresults",
        // "icf",
      ];
      return restrictedTabs.includes(tabKey);
    }

    return false;
  };

  useEffect(() => {
    if (activeTab === "detail" && id) {
      queryClient.refetchQueries({
        queryKey: ["volunteer-select", Number(id)],
      });
    }
  }, [activeTab, id]);
  /* ---------------- Render ---------------- */

  return (
    <>
      <Container fluid>
        <div className={volunteerTabsStyles.study_tab_wrapper}>
          <CommonTabHeader
            title={VolunteerName}
            isOpen={isOpen}
            toggle={() => setIsOpen(!isOpen)}
            activeTab={activeTab}
          />

          <div
            className={`d-flex justify-content-between align-items-center bg-white ${volunteerTabsStyles.study_tab_container} mb-3`}
          >
            <Nav
              innerRef={segmentTabsRef}
              className={volunteerTabsStyles.segmentTabs}
            >
              <div
                className={volunteerTabsStyles.slider}
                style={
                  {
                    "--left": slider.left,
                    "--width": slider.width,
                  } as React.CSSProperties
                }
              />

              {tabs.map((t, index) => (
                <NavItem key={t.key}>
                  <NavLink
                    innerRef={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    className={classnames(volunteerTabsStyles.tab, {
                      [volunteerTabsStyles.active]: activeTab === t.key,
                      [volunteerTabsStyles.disabledTab]: isTabLocked(t.key),
                    })}
                    onClick={() => {
                      if (!isTabLocked(t.key)) {
                        toggleTab(t.key, index);
                      }
                    }}
                    style={{
                      pointerEvents: isTabLocked(t.key) ? "none" : "auto",
                      opacity: isTabLocked(t.key) ? 0.5 : 1,
                      cursor: isTabLocked(t.key) ? "not-allowed" : "pointer",
                    }}
                  >
                    <i className={`${t.icon} me-1`} />
                    {t.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
        </div>
        {/* ---------- Routes ---------- */}
        <Routes>
          <Route
            index
            element={
              <RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
                <VolunteerDashboard isTab={true} />
              </RouteGuard>
            }
          />
          <Route
            path="detail"
            element={
              <RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
                <ViewVolunteerDetails
                  volunteerData={volunteerData}
                  isLoading={isLoading}
                  breadcrumbItems={[
                    { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
                    { text: "Volunteer Info" },
                  ]}
                />
              </RouteGuard>
            }
          />
          <Route
            path="rcf"
            element={<RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
              <VolunteerICF volunteerid={volunteerId} />
            </RouteGuard>}
          />
          <Route path="testresults" element={<RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
            <VolunteerTests />
          </RouteGuard>} />
          <Route path="study" element={<RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
            <VolunteerStudyHistoryList />
          </RouteGuard>} />
          <Route path="study/dashboard/:studyId" element={<RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
            <VolunteerStudyDashboard />
          </RouteGuard>} />
          <Route path="block" element={<RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
            <VolunteerBlockTab volunteerId={volunteerId} />
          </RouteGuard>} />
          <Route
            path="*"
            element={
              <RouteGuard module={PERMISSION_MODULES.VOLUNTEER} action={PERMISSION_ACTIONS.VIEW}>
                <VolunteerDashboard isTab={true} />
              </RouteGuard>
            }
          />
        </Routes>
      </Container>

  
    </>
  );
};

export default VolunteerInfoTab;
